import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';

// Prettify a pathname into a human page label, e.g.
//   /admin/user-activity  → "User Activity"
//   /dashboards/view      → "Dashboards View"
//   /reports/builder      → "Reports Builder"
function labelForPath(pathname) {
  const parts = (pathname || '/').split('/').filter(Boolean);
  if (parts.length === 0) return 'Home';
  // Drop trailing id-looking segments (uuids / long hex / numeric).
  const meaningful = parts.filter(
    (p) => !/^[0-9a-f]{8,}$/i.test(p) && !/^\d+$/.test(p) && !p.includes('_')
  );
  const use = meaningful.length ? meaningful : parts;
  return use
    .slice(-2)
    .map((p) => p.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' ');
}

// Pings the backend so admins can see who is online and what page they're on.
// Fires on route change (debounced) and on a steady interval (~45s).
export default function useActivityHeartbeat(enabled) {
  const location = useLocation();
  const lastSent = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const ping = () => {
      const path = location.pathname + (location.search || '');
      const label = labelForPath(location.pathname);
      lastSent.current = Date.now();
      api.activityHeartbeat(path, label).catch(() => {});
    };

    // Debounce the route-change ping slightly so rapid redirects coalesce.
    const t = setTimeout(ping, 400);
    const interval = setInterval(() => {
      if (Date.now() - lastSent.current >= 40000) ping();
    }, 45000);

    return () => {
      clearTimeout(t);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, location.pathname, location.search]);
}
